export type TokenCollectionId = "variables" | "palette" | "semantic";

export type TokenRow =
  | {
      kind: "single";
      name: string;
      value: string;
    }
  | {
      kind: "semantic";
      name: string;
      light: string;
      dark: string;
    };

export type TokenGroup = {
  id: string;
  label: string;
  count: number;
};

const INVALID_NAME_CHARS = /[;{}]/;

/** Display order for Palette sidebar groups and table sections. */
const PALETTE_GROUP_ORDER = [
  "utility",
  "neutral",
  "overlay",
  "blue",
  "green",
  "yellow",
  "promo-steel",
  "promo-gold",
  "__other__",
] as const;

type PaletteGroupId = (typeof PALETTE_GROUP_ORDER)[number];

type PaletteGroupMeta = {
  id: PaletteGroupId | "__other__";
  label: string;
  order: number;
  subOrder: number;
};

function paletteGroupMeta(name: string): PaletteGroupMeta {
  if (name === "transparent") {
    return { id: "utility", label: "Utility", order: 0, subOrder: 0 };
  }
  const neutralStep = name.match(/^neutral-(\d+)$/);
  if (neutralStep) {
    return {
      id: "neutral",
      label: "Neutral scale",
      order: 1,
      subOrder: Number(neutralStep[1]),
    };
  }
  if (/^neutral-a\d+$/.test(name) || /^white-a\d+$/.test(name)) {
    const alpha = Number(name.match(/a(\d+)/)?.[1] ?? 0);
    const channel = name.startsWith("white-") ? 1000 : 0;
    return { id: "overlay", label: "Overlay / alpha", order: 2, subOrder: channel + alpha };
  }
  if (name.startsWith("blue-")) {
    return { id: "blue", label: "Blue", order: 3, subOrder: Number(name.slice(5)) || 0 };
  }
  if (name === "green-hover" || name.startsWith("green-")) {
    const sub =
      name === "green-hover" ? 900 : Number(name.match(/^green-(\d+)/)?.[1] ?? 0);
    return { id: "green", label: "Green", order: 4, subOrder: sub };
  }
  if (name.startsWith("yellow-")) {
    return { id: "yellow", label: "Yellow", order: 5, subOrder: Number(name.slice(7)) || 0 };
  }
  if (name.startsWith("promo-steel-")) {
    return {
      id: "promo-steel",
      label: "Promo · steel",
      order: 6,
      subOrder: Number(name.slice("promo-steel-".length)) || 0,
    };
  }
  if (name.startsWith("promo-gold-")) {
    const sub = name.endsWith("light") ? 0 : name.endsWith("dark") ? 1 : 2;
    return { id: "promo-gold", label: "Promo · gold", order: 7, subOrder: sub };
  }
  return { id: "__other__", label: "Прочее", order: 99, subOrder: 0 };
}

function paletteGroupSortIndex(id: string): number {
  const idx = PALETTE_GROUP_ORDER.indexOf(id as PaletteGroupId);
  return idx === -1 ? PALETTE_GROUP_ORDER.length : idx;
}

function compareTokenNames(a: string, b: string, collection: TokenCollectionId): number {
  if (collection === "palette") {
    const ga = paletteGroupMeta(a);
    const gb = paletteGroupMeta(b);
    if (ga.order !== gb.order) return ga.order - gb.order;
    if (ga.subOrder !== gb.subOrder) return ga.subOrder - gb.subOrder;
    return a.localeCompare(b);
  }
  return a.localeCompare(b, "ru");
}

export function capitalizeGroup(segment: string): string {
  if (!segment) return segment;
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

export function tokenGroupId(name: string, collection: TokenCollectionId = "variables"): string {
  if (collection === "palette") return paletteGroupMeta(name).id;
  const idx = name.indexOf("-");
  if (idx === -1) return "__other__";
  return name.slice(0, idx);
}

export function tokenGroupLabel(name: string, collection: TokenCollectionId = "variables"): string {
  if (collection === "palette") return paletteGroupMeta(name).label;
  const idx = name.indexOf("-");
  if (idx === -1) return "Прочее";
  return capitalizeGroup(name.slice(0, idx));
}

export function tokenDisplaySuffix(name: string): string {
  const idx = name.indexOf("-");
  if (idx === -1) return name;
  return name.slice(idx + 1);
}

export function ensureTokensRoot(doc: Record<string, unknown>): Record<string, unknown> {
  if (!doc.tokens || typeof doc.tokens !== "object" || Array.isArray(doc.tokens)) {
    doc.tokens = {};
  }
  const tokens = doc.tokens as Record<string, unknown>;
  if (!tokens.variables || typeof tokens.variables !== "object" || Array.isArray(tokens.variables)) {
    tokens.variables = {};
  }
  if (!tokens.palette || typeof tokens.palette !== "object" || Array.isArray(tokens.palette)) {
    tokens.palette = {};
  }
  if (!tokens.semantic || typeof tokens.semantic !== "object" || Array.isArray(tokens.semantic)) {
    tokens.semantic = {};
  }
  const semantic = tokens.semantic as Record<string, unknown>;
  if (!semantic.Light || typeof semantic.Light !== "object" || Array.isArray(semantic.Light)) {
    semantic.Light = {};
  }
  if (!semantic.Dark || typeof semantic.Dark !== "object" || Array.isArray(semantic.Dark)) {
    semantic.Dark = {};
  }
  return tokens;
}

function bucket(
  doc: Record<string, unknown>,
  collection: TokenCollectionId,
): Record<string, unknown> {
  const tokens = ensureTokensRoot(doc);
  if (collection === "variables") return tokens.variables as Record<string, unknown>;
  if (collection === "palette") return tokens.palette as Record<string, unknown>;
  const semantic = tokens.semantic as Record<string, unknown>;
  return semantic;
}

export function validateTokenName(name: string, collection: TokenCollectionId, doc: Record<string, unknown>, except?: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return "Имя не может быть пустым";
  if (INVALID_NAME_CHARS.test(trimmed)) return "Имя не должно содержать ; { }";
  const existing = listTokenNames(doc, collection);
  if (existing.some((n) => n === trimmed && n !== except)) {
    return "Имя уже используется в этой коллекции";
  }
  return null;
}

export function listTokenNames(doc: Record<string, unknown>, collection: TokenCollectionId): string[] {
  if (collection === "semantic") {
    const tokens = ensureTokensRoot(doc);
    const semantic = tokens.semantic as Record<string, Record<string, unknown>>;
    const names = new Set<string>();
    for (const theme of ["Light", "Dark"] as const) {
      for (const key of Object.keys(semantic[theme] ?? {})) names.add(key);
    }
    return [...names].sort();
  }
  const map = bucket(doc, collection);
  return Object.keys(map).sort((a, b) => compareTokenNames(a, b, collection));
}

export function listGroups(doc: Record<string, unknown>, collection: TokenCollectionId): TokenGroup[] {
  const names = listTokenNames(doc, collection);
  const counts = new Map<string, { label: string; count: number }>();
  for (const name of names) {
    const id = tokenGroupId(name, collection);
    const label = tokenGroupLabel(name, collection);
    const entry = counts.get(id) ?? { label, count: 0 };
    entry.count += 1;
    counts.set(id, entry);
  }
  return [...counts.entries()]
    .map(([id, { label, count }]) => ({ id, label, count }))
    .sort((a, b) => {
      if (collection === "palette") {
        return paletteGroupSortIndex(a.id) - paletteGroupSortIndex(b.id);
      }
      return a.label.localeCompare(b.label, "ru");
    });
}

export function listRows(
  doc: Record<string, unknown>,
  collection: TokenCollectionId,
  groupFilter: string | null,
): TokenRow[] {
  const names = listTokenNames(doc, collection).filter((name) => {
    if (!groupFilter) return true;
    return tokenGroupId(name, collection) === groupFilter;
  });

  if (collection === "semantic") {
    const tokens = ensureTokensRoot(doc);
    const semantic = tokens.semantic as Record<string, Record<string, unknown>>;
    return names.map((name) => ({
      kind: "semantic" as const,
      name,
      light: String(semantic.Light?.[name] ?? ""),
      dark: String(semantic.Dark?.[name] ?? ""),
    }));
  }

  const map = bucket(doc, collection);
  return names.map((name) => ({
    kind: "single" as const,
    name,
    value: String(map[name] ?? ""),
  }));
}

export function setTokenValue(
  doc: Record<string, unknown>,
  collection: TokenCollectionId,
  name: string,
  field: "value" | "Light" | "Dark",
  value: string,
): void {
  ensureTokensRoot(doc);
  if (collection === "semantic") {
    const semantic = (doc.tokens as Record<string, unknown>).semantic as Record<string, Record<string, unknown>>;
    const theme = field === "Light" || field === "Dark" ? field : "Light";
    if (!semantic[theme]) semantic[theme] = {};
    if (!value.trim()) {
      delete semantic[theme][name];
      return;
    }
    semantic[theme][name] = value;
    return;
  }
  const map = bucket(doc, collection);
  map[name] = value;
}

export function createToken(
  doc: Record<string, unknown>,
  collection: TokenCollectionId,
  name: string,
  value: string,
): string | null {
  const err = validateTokenName(name, collection, doc);
  if (err) return err;
  ensureTokensRoot(doc);
  if (collection === "semantic") {
    const semantic = (doc.tokens as Record<string, unknown>).semantic as Record<string, Record<string, unknown>>;
    semantic.Light[name] = value;
    semantic.Dark[name] = value;
    return null;
  }
  bucket(doc, collection)[name] = value;
  return null;
}

export function deleteToken(
  doc: Record<string, unknown>,
  collection: TokenCollectionId,
  name: string,
): void {
  ensureTokensRoot(doc);
  if (collection === "semantic") {
    const semantic = (doc.tokens as Record<string, unknown>).semantic as Record<string, Record<string, unknown>>;
    delete semantic.Light?.[name];
    delete semantic.Dark?.[name];
    return;
  }
  delete bucket(doc, collection)[name];
}

export function renameToken(
  doc: Record<string, unknown>,
  collection: TokenCollectionId,
  oldName: string,
  newName: string,
): string | null {
  const err = validateTokenName(newName, collection, doc, oldName);
  if (err) return err;
  if (oldName === newName) return null;

  ensureTokensRoot(doc);
  if (collection === "semantic") {
    const semantic = (doc.tokens as Record<string, unknown>).semantic as Record<string, Record<string, unknown>>;
    for (const theme of ["Light", "Dark"] as const) {
      const map = semantic[theme];
      if (map && Object.prototype.hasOwnProperty.call(map, oldName)) {
        map[newName] = map[oldName];
        delete map[oldName];
      }
    }
  } else {
    const map = bucket(doc, collection);
    if (Object.prototype.hasOwnProperty.call(map, oldName)) {
      map[newName] = map[oldName];
      delete map[oldName];
    }
  }

  replaceTokenRefInDocument(doc, oldName, newName);
  return null;
}

export function replaceTokenRefInDocument(doc: Record<string, unknown>, oldName: string, newName: string): void {
  const oldRef = `@{${oldName}}`;
  const newRef = `@{${newName}}`;
  walkReplaceStrings(doc, (text) => (text.includes(oldRef) ? text.split(oldRef).join(newRef) : text));
}

function walkReplaceStrings(value: unknown, mapFn: (s: string) => string): unknown {
  if (typeof value === "string") return mapFn(value);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      value[i] = walkReplaceStrings(value[i], mapFn);
    }
    return value;
  }
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      obj[key] = walkReplaceStrings(obj[key], mapFn);
    }
  }
  return value;
}

export function isLikelyColorCollection(collection: TokenCollectionId): boolean {
  return collection === "palette" || collection === "semantic";
}
